import { logErrorTelemetry } from "@/hooks/useErrorTelemetry";
import { supabase } from "@/integrations/supabase/client";
import {
  deriveTimeoutMs,
  getEdgeFunctionMetadata,
  type EdgeCallPolicy,
  type EdgeFunctionMetadata,
  type EdgeFunctionName,
} from "@/lib/edge-registry";
import type { EdgeFunctionResult } from "@/types/edge-function";
import { logger, toClientError } from "@/lib/logger";
import { Capacitor } from "@capacitor/core";

const DEFAULT_AUTH_MESSAGE = "Your session expired. Please sign in again.";
// CapacitorHttp on native can be slower to establish connections, especially
// after app backgrounding or rapid navigation. Use a longer timeout on native.
const DEFAULT_TIMEOUT_MS = Capacitor.isNativePlatform() ? 45_000 : 15_000;
// Floor under every derived deadline, for the same reason DEFAULT_TIMEOUT_MS is
// split by platform: a phone on a bad network needs more than a desktop, whatever
// the function's typical latency. Registry expectations used to bypass this
// entirely — a 5s expectation produced a 5s deadline on native, nine times
// shorter than the platform default it was silently overriding.
const MIN_TIMEOUT_MS = Capacitor.isNativePlatform() ? 20_000 : 10_000;
const DEFAULT_REQUEST_METADATA: Omit<
  EdgeFunctionMetadata,
  "name" | "policy" | "retries" | "expectedLatencyMs"
> = {
  authRequired: true,
  userMessageOnFailure: "Unable to process the request right now. Please try again.",
  owner: "platform",
};

export type { EdgeCallPolicy, EdgeFunctionName };

type QueryValue = string | number | boolean | null | undefined;
type QueryParams = Record<string, QueryValue>;

type EdgeFunctionError = {
  message?: string;
  status?: number;
  details?: string;
  code?: string;
  context?: {
    status?: number;
  };
  // Milliseconds until the server's rate-limit window resets, parsed from a
  // 429 response's Retry-After header (seconds) when present. undefined for
  // every non-429 error and for a 429 whose header is absent/unparseable --
  // callers must treat that as "no server-reported delay," not "0ms."
  retryAfterMs?: number;
};

// The header is only readable at all if the responding edge function's CORS
// policy explicitly exposes it (Access-Control-Expose-Headers) -- see
// _shared/cors.ts's own comment on this. A response from a function that
// hasn't opted in reads as if the header were simply absent, same as today.
//
// Only ever parsed for a 429: addRateLimitHeaders (_shared/rate-limit.ts)
// attaches Retry-After to EVERY response it decorates, success or failure,
// not just a rejected one -- stripe-create-checkout's own 502 "Checkout
// session not found" branch goes through the same helper. Without the
// status check, a caller's query-local retryDelay reading retryAfterMs
// couldn't tell a genuine rate-limit rejection from an unrelated 5xx, and
// would wait up to the full rate-limit window before retrying a transient
// upstream failure instead of its intended fast exponential backoff (Codex
// P2, PR #6402, round 21).
const parseRetryAfterMs = (response: Response): number | undefined => {
  if (response.status !== 429) return undefined;
  const header = response.headers.get("Retry-After");
  if (!header) return undefined;
  const seconds = Number(header);
  if (!Number.isFinite(seconds) || seconds < 0) return undefined;
  return seconds * 1000;
};

type EdgeFunctionBody = string | URLSearchParams | ArrayBuffer | Blob | FormData | Record<string, unknown> | null | undefined;

type SerializableBody = BodyInit | string | null | undefined;

const toContentType = (headers: Headers) => headers.get("Content-Type")?.toLowerCase();

const isObject = (value: unknown): value is Record<string, unknown> =>
  Object.prototype.toString.call(value) === "[object Object]";

const toUrlEncodedBody = (payload: Record<string, unknown>) => {
  const form = new URLSearchParams();
  for (const [key, rawValue] of Object.entries(payload)) {
    if (rawValue === undefined || rawValue === null) continue;
    if (typeof rawValue === "string" || typeof rawValue === "number" || typeof rawValue === "boolean") {
      form.set(key, String(rawValue));
      continue;
    }
    form.set(key, JSON.stringify(rawValue));
  }
  return form.toString();
};

const normalizeFetchBody = (body: EdgeFunctionBody, headers: Headers): SerializableBody => {
  if (body == null) return undefined;

  if (body instanceof URLSearchParams) {
    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/x-www-form-urlencoded");
    }
    return body.toString();
  }

  if (body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer) {
    return body;
  }

  const contentType = toContentType(headers);
  if (typeof body === "string") {
    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    return body;
  }

  if (isObject(body)) {
    if (contentType?.includes("application/x-www-form-urlencoded")) {
      return toUrlEncodedBody(body);
    }
    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    return JSON.stringify(body);
  }

  return JSON.stringify(body);
};

export class EdgeFunctionAuthError extends Error {
  readonly redirectTo: string;
  readonly status: number;

  constructor(message: string, redirectTo: string, status = 401) {
    super(message);
    this.name = "EdgeFunctionAuthError";
    this.redirectTo = redirectTo;
    this.status = status;
  }
}

type ErrorLike = {
  name?: string;
  message?: string;
  status?: number;
  context?: {
    status?: number;
  };
};

export type EdgeFunctionInvokeOptions = Parameters<
  typeof supabase.functions.invoke
>[1] & {
  policy?: EdgeCallPolicy;
  public?: boolean;
  timeoutMs?: number;
  retryable?: boolean;
  idempotencyKey?: string;
  traceId?: string;
  query?: QueryParams;
  responseType?: "json" | "text" | "arrayBuffer";
  preserveSessionOnAuthError?: boolean;
};

export const getAuthRedirectUrl = (redirectPath?: string, reason: string = "session-expired") => {
  if (typeof window === "undefined") return "/auth";
  const target = new URL("/auth", window.location.origin);
  const resolvedRedirect = redirectPath ?? `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (resolvedRedirect && resolvedRedirect !== "/auth") {
    target.searchParams.set("redirect", resolvedRedirect);
  }
  if (reason) {
    target.searchParams.set("reason", reason);
  }
  return target.toString();
};

export const getEdgeFunctionStatus = (error: EdgeFunctionError | null | undefined) =>
  error?.context?.status ?? error?.status;

export const getEdgeFunctionRetryAfterMs = (error: EdgeFunctionError | null | undefined) =>
  error?.retryAfterMs;

export const isEdgeFunctionAuthError = (error: unknown): error is EdgeFunctionAuthError => {
  if (error instanceof EdgeFunctionAuthError) return true;
  if (typeof error !== "object" || error === null) return false;
  const candidate = error as ErrorLike;
  return candidate.name === "EdgeFunctionAuthError";
};

const getDefaultMetadata = (name: EdgeFunctionName): EdgeFunctionMetadata => {
  const fromRegistry = getEdgeFunctionMetadata(name);
  return {
    ...DEFAULT_REQUEST_METADATA,
    ...fromRegistry,
  };
};

const calculateRetryDelayMs = (attempt: number) => {
  const baseDelay = 250;
  const multiplier = Math.min(attempt, 6);
  return baseDelay * 2 ** multiplier;
};

const calculateUserImpact = (metadata: EdgeFunctionMetadata) => {
  if (metadata.policy === "interactive") return 9;
  if (metadata.policy === "fire-and-forget") return 5;
  return 6;
};

const AUTH_ERROR_CODES = new Set([
  "session_revoked",
  "invalid_token",
  "token_expired",
  "refresh_token_required",
  "jwt_expired",
  "jwt_invalid",
]);

/**
 * Detect genuinely auth-related error messages.
 *
 * The previous broad regex (`/session|token|auth|expired|revoked/i`) caused
 * false positives on messages like "database session pool exhausted" or
 * "cache entry expired", triggering unnecessary sign-outs.
 *
 * This tighter version matches multi-word phrases that are unambiguously
 * about authentication or session validity.
 */
const isAuthRelatedMessage = (message?: string) => {
  if (!message) return false;
  const lower = message.toLowerCase();
  return (
    lower.includes("session expired") ||
    lower.includes("session revoked") ||
    lower.includes("session not found") ||
    lower.includes("invalid session") ||
    lower.includes("token expired") ||
    lower.includes("token revoked") ||
    lower.includes("invalid token") ||
    lower.includes("invalid refresh token") ||
    lower.includes("refresh token") ||
    lower.includes("jwt expired") ||
    lower.includes("jwt invalid") ||
    lower.includes("not authenticated") ||
    lower.includes("authentication required") ||
    lower.includes("invalid credentials") ||
    lower.includes("invalid claim") ||
    lower.includes("missing authorization")
  );
};

const extractAuthErrorCode = (error: EdgeFunctionError | null, data: unknown, details?: string | null) => {
  if (error && typeof (error as { code?: string }).code === "string") {
    return String((error as { code?: string }).code);
  }

  if (data && typeof data === "object" && "code" in (data as Record<string, unknown>)) {
    const code = (data as Record<string, unknown>).code;
    if (typeof code === "string" && code) {
      return code;
    }
  }

  if (details) {
    try {
      const parsed = JSON.parse(details);
      if (parsed && typeof parsed.code === "string" && parsed.code) {
        return parsed.code;
      }
    } catch {
      // ignore
    }
  }

  return undefined;
};

/**
 * Recover an endpoint's own error payload from a response we already hold.
 *
 * supabase-js reports every non-2xx as a `FunctionsHttpError` whose `message`
 * is the fixed string "Edge Function returned a non-2xx status code"; the real
 * body stays unread in `context`, a `Response`. `resolveErrorMessage` checks
 * `message` first and it is non-empty, so the actionable message never wins.
 *
 * Reading the body was, incidentally, the fallback's job — it re-sent the
 * request and parsed the second response. Endpoints that opt out of the replay
 * need the message without the re-send, so take it from the response already
 * in hand. `clone()` so nothing downstream finds a consumed stream.
 */
const readFunctionsHttpErrorBody = async (
  error: unknown,
): Promise<Record<string, unknown> | null> => {
  const context = (error as { context?: unknown } | null)?.context;
  if (!context || typeof (context as Response).clone !== "function") return null;
  try {
    const body: unknown = await (context as Response).clone().json();
    return body && typeof body === "object" ? (body as Record<string, unknown>) : null;
  } catch {
    // Not JSON, or the stream is gone. The generic message is still returned.
    return null;
  }
};

const isFunctionsHttpError = (error: unknown): error is { name: string; message?: string } => {
  return (
    !!error &&
    typeof error === "object" &&
    (error as { name?: string }).name === "FunctionsHttpError"
  );
};

const isAuthRelatedError = (
  status?: number,
  error: EdgeFunctionError | null = null,
  data: unknown = null,
  details?: string | null,
) => {
  if (status === 401) return true;

  // A 403 can indicate either an authorization (permission) issue or an
  // authentication (session/token) issue.  Only treat it as auth-related
  // when the response contains an explicit auth error code.  Message-based
  // detection is intentionally NOT used for 403 to prevent false sign-outs
  // when a user simply lacks the required role for an action (e.g.,
  // "Forbidden: Platform admin access required").
  if (status === 403) {
    const code = extractAuthErrorCode(error, data, details);
    return !!code && AUTH_ERROR_CODES.has(code);
  }

  const code = extractAuthErrorCode(error, data, details);
  if (code && AUTH_ERROR_CODES.has(code)) return true;

  return isAuthRelatedMessage(error?.message) || false;
};

const toHeaders = (input?: HeadersInit) => {
  if (!input) return new Headers();
  if (input instanceof Headers) return new Headers(input);
  if (Array.isArray(input)) {
    return new Headers(input.map(([key, value]): [string, string] => [key, value]));
  }
  return new Headers(input as Record<string, string>);
};

const headersToRecord = (input?: HeadersInit) => Object.fromEntries(toHeaders(input).entries()) as Record<string, string>;

const getAuthorizationHeader = (headers: Record<string, string>) => {
  for (const [key, value] of Object.entries(headers)) {
    if (key.toLowerCase() === "authorization") {
      return value;
    }
  }
  return null;
};

const bearerTokenFromAuthorization = (authorization: string | null) => {
  const match = authorization?.match(/^Bearer\s+(.+)$/i);
  return match?.[1]?.trim() || null;
};

const addWrapperHeaders = (headers: Headers, traceId: string, idempotencyKey?: string) => {
  headers.set("X-Trace-Id", traceId);
  if (idempotencyKey) {
    headers.set("Idempotency-Key", idempotencyKey);
  }
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
};

const getEdgeFunctionBaseUrl = () => {
  const configuredBase = (import.meta.env.VITE_SUPABASE_URL || (typeof process !== 'undefined' ? process.env.VITE_SUPABASE_URL : undefined) || "").trim();
  const cleanBase = configuredBase.replace(/\/+$/, "");

  const isMissingOrPlaceholder =
    !cleanBase ||
    /your-prod-project\.supabase\.co/i.test(cleanBase) ||
    /your-project\.supabase\.co/i.test(cleanBase);

  if (isMissingOrPlaceholder) {
    throw new Error(
      "Invalid VITE_SUPABASE_URL. Set VITE_SUPABASE_URL to a real Supabase project URL, not a template placeholder.",
    );
  }

  return cleanBase;
};

const appendQueryParams = (name: string, query?: QueryParams) => {
  const base = `${getEdgeFunctionBaseUrl()}/functions/v1/${name}`;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    params.set(key, String(value));
  }
  const queryString = params.toString();
  return queryString ? `${base}?${queryString}` : base;
};

const withRequestTimeout = <T>(operation: () => Promise<T>, timeoutMs?: number): Promise<T> => {
  if (!timeoutMs || timeoutMs <= 0) return operation();

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      const timeoutError = new Error(`Edge function request timed out after ${timeoutMs}ms`);
      (timeoutError as EdgeFunctionError).status = 408;
      reject(timeoutError);
    }, timeoutMs);

    operation()
      .then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });
};

const invokeViaFunctionsClient = async <TData>(
  name: EdgeFunctionName,
  options: Omit<
    EdgeFunctionInvokeOptions,
    "public" | "timeoutMs" | "retryable" | "idempotencyKey" | "traceId" | "query" | "responseType"
  >,
  timeoutMs?: number,
) => {
  // withRequestTimeout races this call against its own setTimeout and
  // rejects on timeout, but that alone never cancels the real HTTP request —
  // supabase.functions.invoke keeps running server-side to completion. Wire
  // a real AbortSignal on the same deadline so a timed-out attempt at least
  // stops the client from waiting on (and paying for) a dead connection.
  // This does NOT guarantee the Edge Function's own server-side work (e.g. a
  // provider send already dispatched) is rolled back — a timeout here is
  // still an ambiguous outcome, not confirmed failure, so callers still need
  // a stable retry key or server-side idempotency before treating it as
  // safe to retry (see #4647).
  const controller = new AbortController();
  const callerSignal = options.signal;
  // Keep a reference to the listener so it can be removed on normal
  // completion — { once: true } only detaches once the signal actually
  // fires 'abort', so a long-lived signal reused across polling/multiple
  // invocations would otherwise accumulate one listener (and this closure's
  // captured AbortController) per completed call.
  let onCallerAbort: (() => void) | null = null;
  if (callerSignal) {
    // Compose rather than overwrite: a caller-provided signal (navigation,
    // component disposal, explicit cancellation) must still be able to abort
    // the request before the timeout deadline.
    if (callerSignal.aborted) {
      controller.abort(callerSignal.reason);
    } else {
      onCallerAbort = () => controller.abort(callerSignal.reason);
      callerSignal.addEventListener("abort", onCallerAbort, { once: true });
    }
  }
  const timer = timeoutMs ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const response = await supabase.functions.invoke<TData>(name, { ...options, signal: controller.signal });
    return response;
  } finally {
    if (timer) clearTimeout(timer);
    if (onCallerAbort) callerSignal?.removeEventListener("abort", onCallerAbort);
  }
};

const invokeViaFetch = async <TData>(
  name: EdgeFunctionName,
  options: EdgeFunctionInvokeOptions,
  metadata: EdgeFunctionMetadata,
) => {
  const {
    method = "GET",
    body,
    headers: rawHeaders,
    query,
    idempotencyKey,
    traceId,
    timeoutMs,
    public: publicMode,
    responseType = "json",
    ..._wrapperOptions
  } = options;

  const baseHeaders = toHeaders(rawHeaders);
  addWrapperHeaders(baseHeaders, traceId ?? "edge-legacy", idempotencyKey);

  if (metadata.authRequired && !publicMode && !baseHeaders.has("Authorization")) {
    const session = (await supabase.auth.getSession()).data.session;
    if (session?.access_token) {
      baseHeaders.set("Authorization", `Bearer ${session.access_token}`);
    }
  }

  if (!baseHeaders.has("apikey")) {
    const anonKey =
      (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined) ??
      (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ??
      (typeof process !== 'undefined' ? process.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined : undefined) ??
      (typeof process !== 'undefined' ? process.env.VITE_SUPABASE_ANON_KEY as string | undefined : undefined);
    if (anonKey) {
      baseHeaders.set("apikey", anonKey);
    }
  }

  if (body != null && !baseHeaders.has("Content-Type") && method !== "GET") {
    baseHeaders.set("Content-Type", "application/json");
  }

  const payload =
    body == null || method === "GET" || method === "HEAD" ? undefined : normalizeFetchBody(body, baseHeaders);
  const requestUrl = appendQueryParams(name, query);
  const controller = new AbortController();
  const timer = timeoutMs ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const response = await fetch(requestUrl, {
      method,
      headers: baseHeaders,
      body: payload,
      signal: controller.signal,
    });

    if (responseType === "arrayBuffer") {
      if (!response.ok) {
        const responseText = await response.text();
        return {
          data: null,
          error: {
            message: responseText || `Failed to call ${name}`,
            status: response.status,
            context: {
              status: response.status,
            },
            retryAfterMs: parseRetryAfterMs(response),
          },
        } as const;
      }

      const arrayBuffer = await response.arrayBuffer();
      return { data: arrayBuffer as TData, error: null } as const;
    }

    if (responseType === "text") {
      const responseText = await response.text();
      if (!response.ok) {
        return {
          data: null,
          error: {
            message: responseText || `Failed to call ${name}`,
            status: response.status,
            context: {
              status: response.status,
            },
            retryAfterMs: parseRetryAfterMs(response),
          },
        } as const;
      }

      return { data: responseText as TData, error: null } as const;
    }

    const responseText = await response.text();
    const decodedResponse =
      responseText.trim() ? (() => {
        try {
          return JSON.parse(responseText);
        } catch {
          return responseText;
        }
      })() : null;

    if (!response.ok) {
      const errorPayload =
        typeof decodedResponse === "object" && decodedResponse !== null ? decodedResponse : {};
      return {
        data: null,
        error: {
          message:
            typeof (errorPayload as { error?: string }).error === "string"
              ? (errorPayload as { error?: string }).error
              : responseText || `Failed to call ${name}`,
          status: response.status,
          context: {
            status: response.status,
          },
          details: typeof errorPayload === "object" ? JSON.stringify(errorPayload) : undefined,
          retryAfterMs: parseRetryAfterMs(response),
        },
      } as const;
    }

    return { data: decodedResponse as TData, error: null } as const;
  } catch (error) {
    if ((error as DOMException)?.name === "AbortError") {
      return {
        data: null,
        error: {
          message: `Edge function request timed out after ${timeoutMs ?? DEFAULT_TIMEOUT_MS}ms`,
          status: 408,
          context: {
            status: 408,
          },
          details: "Request timeout while invoking via direct fetch",
        },
      } as const;
    }

    return {
      data: null,
      error: {
        message: (error as Error)?.message || "Failed to call edge function",
      },
    } as const;
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
};

const parseEnvelope = <TData>(data: TData | null | undefined): EdgeFunctionResult<TData> | null => {
  if (!data || typeof data !== "object") {
    return null;
  }
  const candidate = data as { ok?: boolean; data?: TData; error?: string; details?: string };
  if (typeof candidate.ok !== "boolean") return null;
  return {
    ok: candidate.ok,
    data: candidate.data,
    error: candidate.error,
    details: candidate.details,
  };
};

const SAFE_ERROR_MESSAGE_BY_CODE = new Map<string, string>([
  ["invalid_request", "The request could not be processed."],
  ["invalid_credentials", "Session credentials are invalid. Please sign in again."],
  ["unauthorized", "You are not authorized to perform this action."],
  ["forbidden", "You do not have permission for this action."],
  ["not_found", "The requested resource was not found."],
  ["token_expired", "Your session has expired. Please sign in again."],
  ["token_required", "A valid session is required."],
  ["session_expired", "Your session has expired. Please sign in again."],
  ["rate_limited", "You are making requests too quickly. Please wait."],
  ["validation_failed", "The request values are invalid. Please review and try again."],
  ["service_unavailable", "The service is temporarily unavailable."],
  ["security_block", "This action is temporarily blocked for security reasons."],
  ["internal_error", "An unexpected error occurred. Please try again."],
]);

const normalizeErrorPayload = (errorPayload: unknown, status?: number) => {
  if (errorPayload && typeof errorPayload === "object") {
    return toClientError({
      ...(errorPayload as Record<string, unknown>),
      ...(typeof status === "number" ? { status } : {}),
    }).message;
  }

  if (typeof errorPayload === "string") {
    return toClientError({
      message: errorPayload,
      ...(typeof status === "number" ? { status } : {}),
    }).message;
  }

  return toClientError({
    status,
  }).message;
};

const sanitizeEdgeFunctionMessage = (error: EdgeFunctionError | null, status?: number) => {
  if (error?.code && SAFE_ERROR_MESSAGE_BY_CODE.has(error.code)) {
    return SAFE_ERROR_MESSAGE_BY_CODE.get(error.code)!;
  }
  if (error?.code) return toClientError({ code: error.code, status }).message;
  if (error?.message) return normalizeErrorPayload(error.message, status);
  if (status === 401) return "Authentication required.";
  if (status === 403) return "You are not allowed to perform this action.";
  if (status === 404) return "The requested resource was not found.";
  if (status === 429) return "You are making requests too quickly. Please wait.";
  if (status && status >= 500) return "The service is temporarily unavailable.";
  return "The request could not be completed.";
};

const safeSignOut = async (reason?: string) => {
  try {
    logger.debug("[safeSignOut] Signing out user", { reason: reason ?? "unknown" });
    // Use local scope so an auth error on one device does not cascade
    // and revoke sessions on the user's other devices.
    await supabase.auth.signOut({ scope: "local" });
  } catch {
    // ignore
  }
};

const refreshSessionWithTelemetry = async (
  name: EdgeFunctionName,
  traceId: string,
  metadata: EdgeFunctionMetadata,
) => {
  try {
    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !refreshData.session) {
      const status = (refreshError as { status?: number } | null)?.status ?? 401;
      void logErrorTelemetry({
        errorMessage: `[EdgeFunction:${name}] auth refresh failed`,
        errorName: "EdgeFunctionAuthRefreshError",
        route: typeof window !== "undefined" ? window.location.pathname : "unknown",
        metadata: {
          functionName: name,
          status,
          traceId,
          userImpactScore: calculateUserImpact(metadata),
          owner: metadata.owner,
          policy: metadata.policy,
          retryable: metadata.retries > 0,
          details: refreshError ? (refreshError.message || JSON.stringify(refreshError)) : undefined,
        },
      });
      return false;
    }

    void logErrorTelemetry({
      errorMessage: `[EdgeFunction:${name}] session refreshed`,
      errorName: "EdgeFunctionAuthRefresh",
      route: typeof window !== "undefined" ? window.location.pathname : "unknown",
      metadata: {
        functionName: name,
        traceId,
        userImpactScore: calculateUserImpact(metadata),
        owner: metadata.owner,
        policy: metadata.policy,
        status: 200,
      },
    });
    return true;
  } catch (error) {
    void logErrorTelemetry({
      errorMessage: `[EdgeFunction:${name}] auth refresh threw`,
      errorName: "EdgeFunctionAuthRefreshException",
      route: typeof window !== "undefined" ? window.location.pathname : "unknown",
      metadata: {
        functionName: name,
        traceId,
        userImpactScore: calculateUserImpact(metadata),
        owner: metadata.owner,
        policy: metadata.policy,
        retryable: metadata.retries > 0,
        status: 500,
        details: (error as Error).message,
      },
    });
    return false;
  }
};

const logEdgeFunctionFailure = (
  name: string,
  traceId: string,
  metadata: EdgeFunctionMetadata,
  error: EdgeFunctionError,
  status?: number,
  attempt = 0,
  errorName = "EdgeFunctionError",
) => {
  const score = calculateUserImpact(metadata);
  void logErrorTelemetry({
    errorMessage: `[EdgeFunction:${name}] ${error.message || "Request failed"}`,
    errorName,
    route: typeof window !== "undefined" ? window.location.pathname : "unknown",
    metadata: {
      functionName: name,
      policy: metadata.policy,
      status,
      attempt,
      traceId,
      userImpactScore: score,
      owner: metadata.owner,
      userMessageOnFailure: metadata.userMessageOnFailure,
    },
  });
};

const buildAuthError = (status = 401, message = DEFAULT_AUTH_MESSAGE) =>
  new EdgeFunctionAuthError(message, getAuthRedirectUrl(), status);

const isFunctionResponseEnvelope = <TData>(response: {
  data: TData | null;
  error: EdgeFunctionError | null;
}) => {
  const envelope = parseEnvelope(response.data);
  if (!envelope) return response;
  if (envelope.ok) {
    return {
      ...(response as { data: unknown; error: EdgeFunctionError | null }),
      data: envelope.data as TData,
    };
  }

  return {
    ...(response as { data: unknown; error: EdgeFunctionError | null }),
    data: envelope.data as TData,
    error: {
      message: envelope.error || "Request failed",
      context: {
        status: 422,
      },
      status: 422,
      details: envelope.details,
    },
  };
};

export async function invokeEdgeFunction<TData>(
  name: EdgeFunctionName,
  options?: EdgeFunctionInvokeOptions,
) {
  const metadata = getDefaultMetadata(name);
  const policy = options?.policy ?? metadata.policy;
  const retryLimit = options?.retryable ? metadata.retries : policy === "interactive" ? 0 : metadata.retries;
  const allowRetry = policy === "interactive" ? false : retryLimit > 0;
  const maxRetries = allowRetry ? retryLimit : 0;
  // Precedence: an explicit per-call budget, then a registry override for a
  // function with a known hard ceiling, then a deadline DERIVED from the
  // expectation. Never the expectation itself — see deriveTimeoutMs.
  const defaultTimeout =
    options?.timeoutMs ?? metadata.timeoutMs ?? deriveTimeoutMs(metadata.expectedLatencyMs, MIN_TIMEOUT_MS);
  const effectiveTraceId =
    options?.traceId ??
    (typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `trace-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
  const effectivePublic = options?.public ?? !metadata.authRequired;

  const strippedOptions = {
    ...options,
    policy,
    public: effectivePublic,
    traceId: effectiveTraceId,
    timeoutMs: defaultTimeout,
  };

  let attempt = 0;
  let lastSessionRefresh = false;
  // Declared outside the retry loop and never reset: if an earlier attempt's
  // fallback fired and a later attempt (e.g. after an auth-refresh `continue`)
  // succeeds via the primary path, the invocation still cost the extra
  // rate-limited fallback request and callers need to see that.
  let usedFallback = false;

  while (true) {
    const invokeOptions = { ...strippedOptions };
    const {
      public: publicMode,
      timeoutMs,
      idempotencyKey,
      traceId,
      query,
      responseType,
      preserveSessionOnAuthError,
      ...baseOptions
    } = invokeOptions as EdgeFunctionInvokeOptions;

    // Removed redundant ensureFreshSession() preflight — auth-js's
    // autoRefreshToken: true already handles token refresh on a 30s tick.
    // The preflight here was doubling auth-lock acquisitions and producing
    // the cosmetic [Query] AbortError: Lock broken errors that PR #1402
    // filtered from telemetry. Investigation 2 Option B (this PR) removes
    // the underlying contention at the source. If this call hits a 401
    // because the token expired between auto-refresh ticks, the existing
    // auth-error retry path below handles it with one extra round trip.

    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token;
    const sessionAuthHeader =
      metadata.authRequired && !publicMode && session?.access_token
        ? `Bearer ${session.access_token}`
        : null;

    const callerHeaders = headersToRecord(baseOptions.headers as HeadersInit);
    const callerAuthorizationHeader = getAuthorizationHeader(callerHeaders);
    const callerProvidedAuthorization = callerAuthorizationHeader !== null;
    const callerBearerAccessToken = bearerTokenFromAuthorization(callerAuthorizationHeader);
    const effectiveSessionAuthHeader =
      callerProvidedAuthorization && !lastSessionRefresh ? null : sessionAuthHeader;
    const baseHeaders = {
      // On the first attempt, caller-supplied headers (e.g. a freshly
      // refreshed token from MfaChallenge) take precedence over the
      // auto-detected session token.  After an auth-retry refresh,
      // the session token is newer than the caller's stale explicit
      // header, so the session token should win.
      ...(lastSessionRefresh
        ? { ...callerHeaders, ...(effectiveSessionAuthHeader ? { Authorization: effectiveSessionAuthHeader } : {}) }
        : { ...(effectiveSessionAuthHeader ? { Authorization: effectiveSessionAuthHeader } : {}), ...callerHeaders }),
      "X-Trace-Id": effectiveTraceId,
      ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}),
    };

    const hasQueryParams = query != null && Object.keys(query).length > 0;
    const shouldUseFetch = publicMode || hasQueryParams;
    const shouldSetFunctionsAuth =
      !publicMode &&
      metadata.authRequired &&
      (callerBearerAccessToken || accessToken) &&
      // If the caller explicitly provided Authorization on the first attempt,
      // install that token into the shared functions client instead of the
      // cached Supabase session token. MFA finalization can hold a fresh AAL2
      // token while auth storage still reports the pre-MFA AAL1 token.
      // After an auth-refresh retry, the refreshed cached token should win.
      //
      // Non-Bearer explicit Authorization is left entirely to per-call headers.
      (!callerProvidedAuthorization || lastSessionRefresh || callerBearerAccessToken);
    const functionsAuthToken =
      shouldSetFunctionsAuth
        ? (callerBearerAccessToken && !lastSessionRefresh ? callerBearerAccessToken : accessToken)
        : null;
    if (functionsAuthToken) {
      supabase.functions.setAuth(functionsAuthToken);
    }

    let result: Awaited<ReturnType<typeof supabase.functions.invoke<TData>>>;

    if (shouldUseFetch) {
      result = await withRequestTimeout(
        () =>
          invokeViaFetch<TData>(
            name,
            {
              ...baseOptions,
              headers: baseHeaders,
              method:
                baseOptions.method ??
                (publicMode && baseOptions.body == null ? "GET" : "POST"),
              public: publicMode,
              query,
              idempotencyKey,
              traceId,
              timeoutMs,
              responseType,
            },
            metadata,
          ),
        0,
      );
    } else {
      // Stamped before the request so the body read below can be given what is
      // LEFT of the deadline rather than a fresh copy of it.
      const requestStartedAt = Date.now();
      result = await withRequestTimeout(
        () =>
          invokeViaFunctionsClient<TData>(
            name,
            {
              ...baseOptions,
              headers: baseHeaders,
            } as Omit<
              EdgeFunctionInvokeOptions,
              "public" | "timeoutMs" | "retryable" | "idempotencyKey" | "traceId" | "query" | "responseType"
            >,
            timeoutMs,
          ),
        timeoutMs,
      );

      // An HTTP error response is the endpoint's own answer, not a transport
      // failure, so it is returned as is and never re-sent unless the endpoint
      // is declared `replaySafe: true` (Matt's decision on #5970). Re-sending
      // used to be the default, and it duplicated side effects: a send that
      // reached the provider but answered non-2xx (`deliveredUntracked`) was
      // re-POSTed, the replay hit the first call's idempotency reservation,
      // and its generic failure replaced the response that said the email had
      // already gone out. It also spent single-use resources (#6110).
      //
      // Deliberately checked on the metadata rather than on `retries`: this
      // path never consulted `retries`/`allowRetry`/`policy`, and a registry
      // comment asserting that `retries: 0` covered it is what kept the real
      // mechanism hidden through a whole fix round.
      if (isFunctionsHttpError(result.error) && metadata.replaySafe !== true) {
        // Not replayed, so recover the endpoint's message and details (for
        // example a send's per-recipient results) from the response we
        // already have rather than asking for it again. Without this, every
        // error the endpoint reports is downgraded to "Edge Function returned
        // a non-2xx status code" —
        // which the Stripe callback page then shows and persists as the reason
        // the connection failed (Codex P2, PR #6118).
        // Bounded by what REMAINS of the request deadline.
        //
        // `supabase.functions.invoke` resolves as soon as it has the headers,
        // WITHOUT consuming the body — which clears both timers around the
        // primary invocation. So a response whose body then stalls mid-stream
        // would otherwise have no deadline at all and hang the call
        // indefinitely, defeating the configured timeoutMs (Codex P2, #6118).
        //
        // The remaining budget, not `timeoutMs` again: this read starts only
        // after the invocation above has already had up to the full deadline,
        // so handing it another full one lets a call configured for 30s run for
        // nearly 60 (Codex P2, #6118, round 2).
        //
        // A body that cannot be read inside the budget is simply absent — the
        // generic message stands rather than the request never returning. The
        // zero case is handled explicitly because withRequestTimeout treats a
        // non-positive budget as "no timeout", which is the very hang this
        // guards against.
        const remainingMs = timeoutMs ? timeoutMs - (Date.now() - requestStartedAt) : 0;
        const body =
          !timeoutMs || remainingMs > 0
            ? await withRequestTimeout(
                () => readFunctionsHttpErrorBody(result.error),
                remainingMs,
              ).catch(() => null)
            : null;
        const bodyMessage =
          typeof body?.error === "string" && body.error.trim()
            ? body.error
            : typeof body?.message === "string" && body.message.trim()
              ? body.message
              : null;
        // Retry-After comes from the response we already have, so a 429's
        // own wait reaches callers that pace on it without a second request
        // (Codex P2, PR #8321).
        const context = (result.error as { context?: unknown }).context;
        const retryAfterMs =
          context instanceof Response ? parseRetryAfterMs(context) : undefined;
        // The fetch path always set a top-level status too, and callers branch
        // on it (useSolomonPlanner's 403 upgrade response) (Codex P2, PR #8321).
        const status = context instanceof Response ? context.status : undefined;
        if (body || retryAfterMs !== undefined || status !== undefined) {
          result = {
            ...result,
            error: Object.assign(Object.create(Object.getPrototypeOf(result.error)), result.error, {
              // `context` is left as the original Response so
              // getEdgeFunctionStatus still reads its status.
              message: bodyMessage ?? (result.error as { message?: string }).message,
              ...(status !== undefined ? { status } : {}),
              // The body as a JSON string, the same contract the fetch path
              // has always kept: resultsFromEdgeError and other callers
              // JSON.parse it to recover per-recipient results (Codex P1,
              // PR #8321).
              ...(body ? { details: JSON.stringify(body) } : {}),
              ...(retryAfterMs !== undefined ? { retryAfterMs } : {}),
            }),
          } as typeof result;
        }
      } else if (isFunctionsHttpError(result.error)) {
        // Declared replay-safe: the identical request may be sent again over
        // plain fetch. That second request counts against the same rate-limit
        // budget, so it is marked on the result (see #4647) for callers that
        // budget for it.
        usedFallback = true;
        const fallbackResult = await withRequestTimeout(
          () =>
            invokeViaFetch<TData>(
              name,
              {
                ...baseOptions,
                headers: baseHeaders,
                method:
                  baseOptions.method ??
                  (publicMode && baseOptions.body == null ? "GET" : "POST"),
                public: publicMode,
                query: {},
                idempotencyKey,
                traceId,
                timeoutMs,
                responseType,
              },
              metadata,
            ),
          0,
        );

        result = fallbackResult as Awaited<ReturnType<typeof supabase.functions.invoke<TData>>>;
      }
    }

    // Annotated as an intersection (not inferred from the object literal)
    // so usedFallback lands on every branch of the underlying union without
    // narrowing data/error's own type — narrowing them previously unmasked
    // unrelated pre-existing type mismatches in several callers. Set
    // whenever the FunctionsHttpError fallback fired this attempt, so
    // callers with their own rate-limit budget (e.g. bulk dispatch) can
    // account for the extra request instead of assuming 1 call == 1 slot.
    const normalizedResult: Awaited<ReturnType<typeof supabase.functions.invoke<TData>>> & {
      usedFallback: boolean;
    } = {
      ...(isFunctionResponseEnvelope({
        data: result.data as TData | null,
        error: result.error as EdgeFunctionError | null,
      }) as Awaited<ReturnType<typeof supabase.functions.invoke<TData>>>),
      usedFallback,
    };
    const status = getEdgeFunctionStatus(normalizedResult.error as EdgeFunctionError);
    const currentData = normalizedResult.data as TData | null;
    const authErrorCode = extractAuthErrorCode(
      normalizedResult.error as EdgeFunctionError | null,
      currentData,
      normalizedResult.error?.details,
    );
    attempt += 1;

    if (!normalizedResult.error) {
      if ((currentData as Record<string, unknown>)?.code === "session_revoked") {
        await safeSignOut("session_revoked response from edge function: " + name);
        const authError = buildAuthError(401);
        logEdgeFunctionFailure(
          name,
          effectiveTraceId,
          metadata,
          {
            message: authError.message,
            status: 401,
            context: {
              status: 401,
            },
            details: "Function response indicates session was revoked.",
          },
          401,
          attempt,
          "EdgeFunctionAuthError",
        );
        return {
          ...normalizedResult,
          error: authError,
          data: null,
        };
      }

      return normalizedResult;
    }

    if (
      isAuthRelatedError(
        status,
        normalizedResult.error as EdgeFunctionError | null,
        currentData,
        normalizedResult.error?.details,
      ) &&
      metadata.authRequired &&
      !publicMode &&
      !lastSessionRefresh
    ) {
      const didRefresh = await refreshSessionWithTelemetry(name, effectiveTraceId, metadata);
      if (didRefresh) {
        lastSessionRefresh = true;
        void logErrorTelemetry({
          errorMessage: `[EdgeFunction:${name}] refreshed session and retrying`,
          errorName: "EdgeFunctionAuthRefresh",
          route: typeof window !== "undefined" ? window.location.pathname : "unknown",
          metadata: {
            functionName: name,
            traceId: effectiveTraceId,
            userImpactScore: calculateUserImpact(metadata),
            policy: metadata.policy,
            owner: metadata.owner,
            status,
            authErrorCode,
          },
        });
        continue;
      }
    }

    if (
      isAuthRelatedError(status, normalizedResult.error as EdgeFunctionError | null, currentData, normalizedResult.error?.details) &&
      metadata.authRequired &&
      !publicMode
    ) {
      // Only force sign-out when the caller has explicitly opted in via
      // preserveSessionOnAuthError === false.  Defaulting to sign-out on
      // *any* auth error after a failed refresh is too aggressive — transient
      // 401s or JWT-expiry during background work (notifications, syncs)
      // should not nuke the user's entire session.  The explicit
      // session_revoked check (line ~847) already handles true revocations.
      if (preserveSessionOnAuthError === false) {
        await safeSignOut("auth error after failed refresh for edge function: " + name);
      }
      const authError = buildAuthError(status ?? 401);
      logEdgeFunctionFailure(
        name,
        effectiveTraceId,
        metadata,
        {
          message: authError.message,
          status: status ?? 401,
          context: {
            status: status ?? 401,
          },
          details: normalizedResult.error?.details ?? "Auth refresh failed for edge function request.",
        },
        status ?? 401,
        attempt,
        "EdgeFunctionAuthError",
      );
      return {
        ...normalizedResult,
        data: null,
        error: authError,
      };
    }

    const shouldRetry =
      !publicMode &&
      policy !== "interactive" &&
      allowRetry &&
      attempt <= maxRetries + 1 &&
      (status === 429 || status === 502 || status === 503 || status === 504 || !status);

    if (!shouldRetry) {
      logEdgeFunctionFailure(name, effectiveTraceId, metadata, normalizedResult.error as EdgeFunctionError, status, attempt);
      return normalizedResult;
    }

    await new Promise((resolve) => setTimeout(resolve, calculateRetryDelayMs(attempt - 1)));
  }
}

export const invokePublicEdgeFunction = <TData>(
  name: EdgeFunctionName,
  options?: Omit<
    EdgeFunctionInvokeOptions,
    "public" | "policy" | "retryable" | "idempotencyKey" | "traceId"
  >,
) =>
  invokeEdgeFunction<TData>(name, {
    ...options,
    public: true,
    policy: "interactive",
    retryable: false,
  });
